import { useState } from 'react';

import type { JobInboundSettingsApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Label } from '@/modules/ui/components/label/Label';
import { SavedIndicator } from '@/modules/ui/components/saved-indicator/SavedIndicator';
import { Switch } from '@/modules/ui/components/switch/Switch';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';

export interface JobInboundSettingsValues {
  isInboundEnabled: boolean;
  publicJobDescription: string | null;
}

interface JobInboundSettingsProps {
  settings: JobInboundSettingsApi;
  isPending: boolean;
  isSaved: boolean;
  error: string | null;
  onSave: (values: JobInboundSettingsValues) => void;
  onRetry: () => void;
}

/**
 * Staff control for a job's inbound setting and Public Job Description. The
 * switch saves on change; the description saves with its own button. Each save
 * sends the other value as last saved. "Saved" and failures show beside the
 * control that was used last.
 */
export function JobInboundSettings({
  settings,
  isPending,
  isSaved,
  error,
  onSave,
  onRetry,
}: JobInboundSettingsProps) {
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null);
  const [lastUsed, setLastUsed] = useState<'toggle' | 'description'>('toggle');
  const savedDescription = settings.publicJobDescription ?? '';
  const description = descriptionDraft ?? savedDescription;
  // The API stores a blank description as none.
  const isDescriptionDirty =
    (description.trim() === '' ? '' : description) !== savedDescription;

  const handleToggle = (isInboundEnabled: boolean) => {
    setLastUsed('toggle');
    onSave({
      isInboundEnabled,
      publicJobDescription: settings.publicJobDescription ?? null,
    });
  };

  const handleSaveDescription = () => {
    setLastUsed('description');
    onSave({
      isInboundEnabled: settings.isInboundEnabled,
      publicJobDescription: description,
    });
  };

  const errorAt = (control: typeof lastUsed) =>
    error && lastUsed === control ? (
      <div className="flex flex-wrap items-center gap-2">
        <InlineError>{error}</InlineError>
        <Button size="sm" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      </div>
    ) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Inbound applications</CardTitle>
        <CardDescription>
          Dev only. When on, the job accepts applications while it is published
          and not a draft.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <Label htmlFor="job-inbound-toggle" className="text-base">
              Accept applications
            </Label>
            <div className="flex shrink-0 items-center gap-3">
              <SavedIndicator isSaved={isSaved && lastUsed === 'toggle'} />
              <Switch
                id="job-inbound-toggle"
                checked={settings.isInboundEnabled}
                disabled={isPending}
                onCheckedChange={handleToggle}
              />
            </div>
          </div>
          {errorAt('toggle')}
        </div>

        <div className="space-y-2">
          <Label htmlFor="public-job-description">Public Job Description</Label>
          <Textarea
            id="public-job-description"
            className="min-h-32 leading-6"
            maxLength={10_000}
            placeholder="Shown to applicants. Leave empty to show only the title, location and contract details."
            value={description}
            onChange={(event) => setDescriptionDraft(event.target.value)}
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-muted-foreground text-sm">
              The internal job description is never shown to applicants.
            </p>
            <div className="flex shrink-0 items-center gap-3">
              <SavedIndicator isSaved={isSaved && lastUsed === 'description'} />
              {isDescriptionDirty && (
                <Button
                  size="sm"
                  loading={isPending}
                  onClick={handleSaveDescription}
                >
                  Save description
                </Button>
              )}
            </div>
          </div>
          {errorAt('description')}
        </div>
      </CardContent>
    </Card>
  );
}
